import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { 
  MapPin, Users, Book, Lightbulb, Star, TreePine, 
  Globe, Clock, Heart, CheckCircle, ArrowRight, Map,
  Award, Navigation, Anchor, Download, Save, UserPlus, Scroll
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AnalysisResults = ({ analysis }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isSavingToProfile, setIsSavingToProfile] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Debug logging
  console.log('AnalysisResults rendering:', {
    hasAnalysis: !!analysis,
    hasResults: !!analysis?.results,
    analysisKeys: analysis ? Object.keys(analysis) : [],
    completedAt: analysis?.completed_at
  });

  if (!analysis || !analysis.results) {
    return (
      <Card className="bg-white shadow-lg">
        <CardContent className="p-8 text-center">
          <p className="text-gray-500">No analysis results available.</p>
        </CardContent>
      </Card>
    );
  }

  const { results } = analysis;
  
  // Calculate the highest tribal match percentage
  const getHighestTribalMatch = () => {
    let highestMatch = 0;
    let topTribe = "Unknown";
    
    // Check primary tribal analysis
    if (results.primary_tribal_analysis?.confidence_score) {
      highestMatch = results.primary_tribal_analysis.confidence_score * 100;
      topTribe = results.primary_tribal_analysis.primary_tribal_affiliation;
    }
    
    // Check tribal connections for higher matches
    if (results.tribal_connections) {
      results.tribal_connections.forEach(connection => {
        const matchPercentage = (connection.confidence || 0) * 100;
        if (matchPercentage > highestMatch) {
          highestMatch = matchPercentage;
          topTribe = connection.tribe;
        }
      });
    }
    
    // Check regional mapping if available
    if (results.regional_mapping_results?.confidence_level) {
      const regionalMatch = results.regional_mapping_results.confidence_level * 100;
      if (regionalMatch > highestMatch) {
        highestMatch = regionalMatch;
        topTribe = results.regional_mapping_results.tribal_affiliation || topTribe;
      }
    }
    
    return { percentage: Math.round(highestMatch), tribe: topTribe };
  };
  
  const topTribalMatch = getHighestTribalMatch();


  // Download Heritage PDF
  const downloadHeritagePdf = async () => {
    try {
      toast({
        title: "Generating Your Heritage Document...",
        description: "Creating a beautiful PDF of your ancestral analysis.",
      });

      // Get user profile data
      const profileData = {
        tribal_affiliations: user.profile_data?.tribal_affiliations || '',
        lineage_info: user.profile_data?.lineage_info || '',
        interests: user.profile_data?.interests || ''
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lineage/generate-lineage-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_name: user.full_name || 'Heritage Seeker',
          analysis_data: analysis,
          profile_data: profileData
        }),
      });

      if (!response.ok) {
        throw new Error(`PDF generation failed: ${response.status}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const userName = user.full_name || 'Heritage_Seeker';
      const safeName = userName.replace(/[^a-zA-Z0-9 -]/g, '').replace(/\s+/g, '_').substring(0, 50);
      link.download = `TribalRoot_Heritage_${safeName}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "✅ Heritage Document Downloaded!",
        description: "Your ancestral heritage has been saved as a PDF.",
      });
      
    } catch (error) {
      console.error('Error downloading heritage PDF:', error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: "Unable to generate PDF at this time. Please try again.",
      });
    }
  };

  // Save Analysis to Profile
  const saveToProfile = async () => {
    // Allow admins to bypass subscription check
    const subscription_tier = user?.subscription_tier || 'free';
    const isAdmin = user?.role === 'admin';
    
    if (subscription_tier === 'free' && !isAdmin) {
      toast({
        variant: "destructive",
        title: "Upgrade Required",
        description: "Only Seekers Path and Crown subscribers can save analysis to their profile.",
      });
      return;
    }

    setIsSavingToProfile(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Please log in to save to profile');
      }

      const response = await fetch(`${BACKEND_URL}/api/genealogy/save-to-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          analysis_result: results,
          analysis_type: 'combined'  // Since we're merging tribal and surname data
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to save: ${response.status}`);
      }

      const data = await response.json();
      
      toast({
        title: "✅ Saved to Profile!",
        description: `Your ancestral analysis has been added to your public profile. ${data.tribes_saved} tribes and ${data.surnames_saved} surnames saved.`,
      });
      
    } catch (error) {
      console.error('Error saving to profile:', error);
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: error.message || "Unable to save to profile at this time. Please try again.",
      });
    } finally {
      setIsSavingToProfile(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Header with Confidence Score */}
      <Card className="bg-gradient-to-r from-green-700 to-green-800 text-white shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Your Ancestral Analysis Results</h2>
              <p className="text-green-100">
                {analysis.completed_at ? (
                  <>Completed on {new Date(analysis.completed_at).toLocaleDateString()}</>
                ) : (
                  <>Analysis Complete</>
                )}
              </p>
              <div className="flex gap-3 mt-4">
                <Button 
                  onClick={downloadHeritagePdf}
                  variant="outline"
                  className="text-white border-white hover:bg-white hover:text-green-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Save Heritage PDF
                </Button>
                <Button 
                  onClick={saveToProfile}
                  disabled={isSavingToProfile}
                  variant="outline"
                  className="text-white border-white hover:bg-white hover:text-green-700"
                >
                  {isSavingToProfile ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Save to Profile
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">{topTribalMatch.percentage}%</div>
              <div className="text-sm text-green-100">Tribal Match</div>
              <div className="text-xs text-green-200 mt-1">{topTribalMatch.tribe}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Results Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tribal">Tribal Connections</TabsTrigger>
          <TabsTrigger value="etymology">Name Origins</TabsTrigger>
          <TabsTrigger value="recommendations">Next Steps</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Primary Regional Analysis Results */}
          {results.primary_tribal_analysis && (
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 shadow-lg border-2 border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Award className="w-6 h-6 mr-2 text-amber-600" />
                  Primary Tribal Identification
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {results.primary_tribal_analysis.primary_tribal_affiliation}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Map className="w-4 h-4 mr-2" />
                        {results.primary_tribal_analysis.region_name || 'Regional Analysis'}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        {results.primary_tribal_analysis.traditional_territory}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Globe className="w-4 h-4 mr-2" />
                        {results.primary_tribal_analysis.language_family} Language Family
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="mb-4">
                      <div className="text-4xl font-bold text-amber-600">
                        {Math.round(results.primary_tribal_analysis.confidence_score * 100)}%
                      </div>
                      <div className="text-sm text-gray-600">Tribal Match</div>
                    </div>
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      {results.primary_tribal_analysis.analysis_basis}
                    </Badge>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-white rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Cultural Notes</h4>
                  <p className="text-gray-600 text-sm">{results.primary_tribal_analysis.cultural_notes}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Regional Mapping Summary */}
          {results.regional_mapping_results && (
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Navigation className="w-5 h-5 mr-2 text-blue-600" />
                  Regional Analysis Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      Region {results.regional_mapping_results.identified_region || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-600">Identified Region</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-lg font-semibold text-green-600">
                      {results.regional_mapping_results.tribal_affiliation}
                    </div>
                    <div className="text-sm text-gray-600">Tribal Affiliation</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {Math.round(results.regional_mapping_results.confidence_level * 100)}%
                    </div>
                    <div className="text-sm text-gray-600">Tribal Match</div>
                  </div>
                </div>
                {results.regional_mapping_results.maternal_great_grandmother_location && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">
                      <strong>Analysis Based On:</strong> Maternal Great Grandmother's birth location - {results.regional_mapping_results.maternal_great_grandmother_location}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tribal Connections Summary */}
          {results.tribal_connections && results.tribal_connections.length > 0 && (
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2 text-green-600" />
                  Identified Tribal Connections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.tribal_connections.slice(0, 4).map((connection, index) => (
                    <div key={index} className={`border rounded-lg p-4 ${connection.is_primary ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <h3 className="font-semibold text-gray-900">{connection.tribe}</h3>
                          {connection.is_primary && (
                            <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 text-xs">Primary</Badge>
                          )}
                        </div>
                        <Badge variant="secondary">
                          {(connection.confidence * 100).toFixed(0)}% match
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{connection.evidence}</p>
                      <div className="flex items-center text-xs text-gray-500 mb-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {connection.geographic_connection}
                      </div>
                      {connection.language_family && (
                        <div className="text-xs text-gray-500">
                          Language: {connection.language_family}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Family Tree Insights */}
          {results.family_tree_insights && (
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TreePine className="w-5 h-5 mr-2 text-green-600" />
                  Family Tree Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {results.family_tree_insights.primary_tribal_identification && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-800 mb-2">Primary Tribal Identification</h4>
                    <p className="text-green-700 text-sm">{results.family_tree_insights.primary_tribal_identification}</p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Maternal Lineage Analysis</h4>
                  <p className="text-gray-600">{results.family_tree_insights.maternal_lineage_analysis || results.family_tree_insights.maternal_line_analysis}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Paternal Line Analysis</h4>
                  <p className="text-gray-600">{results.family_tree_insights.paternal_line_analysis}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Regional Connections</h4>
                  <p className="text-gray-600">{results.family_tree_insights.regional_connections}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tribal Connections Tab */}
        <TabsContent value="tribal" className="space-y-6">
          {results.tribal_connections && results.tribal_connections.map((connection, index) => (
            <Card key={index} className={`bg-white shadow-lg ${connection.is_primary ? 'border-2 border-amber-300' : ''}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <Star className={`w-5 h-5 mr-2 ${connection.is_primary ? 'text-amber-500' : 'text-gray-400'}`} />
                    {connection.tribe}
                    {connection.is_primary && (
                      <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800">Primary Connection</Badge>
                    )}
                  </CardTitle>
                  <div className="text-right">
                    <Progress value={connection.confidence * 100} className="w-24 mb-1" />
                    <div className="text-sm text-gray-500">{(connection.confidence * 100).toFixed(0)}% tribal match</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Evidence & Analysis Method</h4>
                  <p className="text-gray-600">{connection.evidence}</p>
                  {connection.analysis_method && (
                    <div className="mt-2">
                      <Badge variant="outline" className="text-xs">{connection.analysis_method}</Badge>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      Historical Period
                    </h4>
                    <p className="text-gray-600">{connection.historical_period}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      Geographic Connection
                    </h4>
                    <p className="text-gray-600">{connection.geographic_connection}</p>
                  </div>
                </div>

                {/* Enhanced Regional Information */}
                {connection.region_id && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Map className="w-4 h-4 mr-1" />
                      Regional Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><strong>Region ID:</strong> {connection.region_id}</div>
                      <div><strong>Language Family:</strong> {connection.language_family}</div>
                    </div>
                    {connection.region_name && (
                      <div className="mt-2 text-sm"><strong>Region:</strong> {connection.region_name}</div>
                    )}
                  </div>
                )}

                {/* Cultural Notes */}
                {connection.cultural_notes && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Cultural Heritage</h4>
                    <p className="text-gray-700 text-sm">{connection.cultural_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Etymology Tab - Enhanced with Historical Research */}
        <TabsContent value="etymology" className="space-y-6">
          {results.etymology && Object.keys(results.etymology).length > 0 ? (
            Object.entries(results.etymology).map(([surname, analysis]) => (
              <Card key={surname} className="bg-white shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center">
                      <Book className="w-5 h-5 mr-2 text-blue-600" />
                      {surname} - Historical Analysis
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Early Origins of the Family Name - NEW SECTION */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-5 rounded-lg border-2 border-amber-200">
                    <h4 className="font-bold text-amber-900 mb-3 flex items-center text-lg">
                      <Scroll className="w-5 h-5 mr-2" />
                      📜 Early Origins of the Family Name
                    </h4>
                    <div className="space-y-3">
                      {analysis.european_origin && (
                        <>
                          <p className="text-gray-800 leading-relaxed">
                            The surname <strong>{surname}</strong> originates from <strong>{analysis.european_origin.country}</strong>, 
                            specifically from the <strong>{analysis.european_origin.region}</strong> region. 
                            In the <strong>{analysis.european_origin.language}</strong> language, this name means "{analysis.european_origin.meaning}".
                          </p>
                          {analysis.european_origin.first_recorded && (
                            <p className="text-gray-700 text-sm">
                              <strong>First Historical Record:</strong> {analysis.european_origin.first_recorded}
                            </p>
                          )}
                        </>
                      )}
                      {analysis.linguistic_analysis && (
                        <p className="text-gray-700 mt-2">
                          <strong>Etymology:</strong> {analysis.linguistic_analysis}
                        </p>
                      )}
                      {analysis.historical_context && (
                        <p className="text-gray-700 mt-2 italic border-l-4 border-amber-300 pl-3">
                          {analysis.historical_context}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* European Origins */}
                  {analysis.european_origin && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                        <Globe className="w-4 h-4 mr-2" />
                        European Origins
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div><strong>Country:</strong> {analysis.european_origin.country}</div>
                        <div><strong>Region:</strong> {analysis.european_origin.region}</div>
                        <div><strong>Language:</strong> {analysis.european_origin.language}</div>
                        <div><strong>First Recorded:</strong> {analysis.european_origin.first_recorded}</div>
                        <div className="md:col-span-2">
                          <strong>Meaning:</strong> {analysis.european_origin.meaning}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Linguistic Analysis */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Linguistic Analysis</h4>
                    <p className="text-gray-600">{analysis.linguistic_analysis}</p>
                  </div>

                  {/* Historical Context */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Historical Context</h4>
                    <p className="text-gray-600">{analysis.historical_context}</p>
                  </div>

                  {/* American Adoption */}
                  {analysis.american_adoption && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-2">American Adoption</h4>
                      <p className="text-green-800 text-sm">{analysis.american_adoption}</p>
                    </div>
                  )}

                  {/* Indigenous Connections */}
                  {analysis.indigenous_connections && analysis.indigenous_connections.length > 0 && (
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="font-medium text-amber-900 mb-2">Indigenous Connections</h4>
                      <ul className="text-amber-800 text-sm space-y-1">
                        {analysis.indigenous_connections.map((connection, index) => (
                          <li key={index} className="flex items-start">
                            <Star className="w-3 h-3 mr-2 mt-1 flex-shrink-0" />
                            {connection}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Historical Variations */}
                  {analysis.variations && analysis.variations.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Historical Spelling Variations</h4>
                      <div className="flex flex-wrap gap-2">
                        {analysis.variations.map((variation, index) => (
                          <Badge key={index} variant="outline">{variation}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Research Sources */}
                  {analysis.sources && analysis.sources.length > 0 && (
                    <div className="text-xs text-gray-500">
                      <strong>Sources:</strong> {analysis.sources.join(', ')}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Book className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No etymology analysis available.</p>
              </CardContent>
            </Card>
          )}

          {/* Detailed Surname Research Results */}
          {results.surname_research_results && (
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Book className="w-5 h-5 mr-2 text-purple-600" />
                  Comprehensive Surname Research
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(results.surname_research_results).map(([surname, research]) => (
                  <div key={surname} className="border-l-4 border-purple-300 pl-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">{surname} Family Research</h4>
                    
                    {/* European Origins */}
                    {research.european_origins && research.european_origins.length > 0 && (
                      <div className="mb-4">
                        <h5 className="font-medium text-gray-800 mb-2">European Origins</h5>
                        {research.european_origins.map((origin, index) => (
                          <div key={index} className="bg-blue-50 p-3 rounded mb-2">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div><strong>Country:</strong> {origin.country}</div>
                              <div><strong>Language:</strong> {origin.language}</div>
                              <div className="col-span-2"><strong>Meaning:</strong> {origin.meaning}</div>
                              {origin.first_recorded && (
                                <div className="col-span-2"><strong>First Recorded:</strong> {origin.first_recorded}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* American Presence */}
                    {research.american_presence && (
                      <div className="mb-4">
                        <h5 className="font-medium text-gray-800 mb-2">American Presence</h5>
                        <div className="bg-green-50 p-3 rounded">
                          <div className="text-sm space-y-2">
                            <div><strong>Arrival Period:</strong> {research.american_presence.arrival_period}</div>
                            {research.american_presence.entry_ports && research.american_presence.entry_ports.length > 0 && (
                              <div><strong>Entry Ports:</strong> {research.american_presence.entry_ports.join(', ')}</div>
                            )}
                            {research.american_presence.settlement_areas && research.american_presence.settlement_areas.length > 0 && (
                              <div><strong>Settlement Areas:</strong> {research.american_presence.settlement_areas.slice(0, 3).join(', ')}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Historical Variants */}
                    {research.historical_variants && research.historical_variants.length > 0 && (
                      <div className="mb-4">
                        <h5 className="font-medium text-gray-800 mb-2">Historical Spelling Variants</h5>
                        <div className="flex flex-wrap gap-2">
                          {research.historical_variants.map((variant, index) => (
                            <Badge key={index} variant="outline" className="text-xs">{variant}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-end text-xs text-gray-500 pt-2 border-t">
                      <span>Sources: {research.sources ? research.sources.length : 0}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>


        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-6">
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-yellow-600" />
                Personalized Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4">
                {results.recommendations && results.recommendations.map((recommendation, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <ArrowRight className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">{recommendation}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Next Steps */}
          {results.next_steps && (
            <Card className="bg-gradient-to-br from-green-50 to-blue-50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  Suggested Next Steps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {results.next_steps.map((step, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <p className="text-gray-700 font-medium">{step}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalysisResults;